import type {HotelDetails} from '@/api/hoteldetails';
import type {
  ProductEcommerceItem,
  BestOfferTypeName,
} from '@/redux/middleware/googleTagManager/types';
import type {Availability} from '@/redux/v1/availability/models';
import type {Price} from '@/redux/v1/models';
import type {FlightRoute, Offer} from '@/redux/v1/offers/list/models';

import {mapHotelCategory} from '@/redux/middleware/googleTagManager/utils/mapHotelCategory';
import {mapToSortListNameForOffer} from '@/redux/middleware/googleTagManager/utils/mapToSortListNameForOffer';

export type MapProductEcommerceItemProps = {
  hotelId?: string;
  hotelData?: HotelDetails;
  offerData?: null | Availability | Offer;
  travellerCount: number;
  index?: number;
  supplier?: null | string;
  itemBestOfferType: BestOfferTypeName;
  sortParams: null | string;
  upsellingOfferPrice?: null | Price;
  upsellingUpgradeIds?: Array<string>;
  alternativeFlightPrice?: null | Price;
};

const getFlightType = (flightRoute: FlightRoute): string =>
  flightRoute.directFlightAssumed
    ? 'Direktflug'
    : `${flightRoute.numberOfStops} Stopp(s)`;

export const mapProductEcommerceItem = ({
  hotelId,
  hotelData,
  offerData,
  travellerCount,
  index,
  supplier,
  itemBestOfferType,
  sortParams,
  upsellingOfferPrice,
  upsellingUpgradeIds = [],
  alternativeFlightPrice,
}: MapProductEcommerceItemProps): ProductEcommerceItem => {
  const totalPrice =
    offerData && offerData?.totalPrice?.amount
      ? offerData.totalPrice.amount / 100
      : 0;
  const currency =
    offerData && offerData?.totalPrice?.currency
      ? offerData.totalPrice.currency
      : '';
  const sustainability = hotelData?.isSustainable ? '1' : '0';
  // @FIXME what about multiple rooms? How should these be tracked?
  const firstRoom = offerData?.rooms?.[0];

  return {
    item_name: hotelData?.name || '',
    item_id: hotelId || hotelData?.id || '',
    currency,
    item_list_name: mapToSortListNameForOffer(sortParams ?? ''),
    item_bestoffer_type: itemBestOfferType,
    item_category: mapHotelCategory(
      hotelData?.category,
      hotelData?.categoryType
    ),
    item_category2:
      Object.values(offerData?.tourOperatorSpecials || {})
        .flatMap((specials) => specials.map((special) => special.type))
        .join(',') || 'no specials',
    location_id: '',
    quantity: 1,
    price: totalPrice,
    item_category3: String(
      Math.round((totalPrice / travellerCount) * 100) / 100
    ),
    item_category4: firstRoom?.boardType?.type || '',
    item_category5: supplier || '',
    item_sustainability: sustainability,
    item_variant:
      offerData?.flight?.inbound && offerData.flight?.outbound
        ? [
            getFlightType(offerData.flight.inbound),
            getFlightType(offerData.flight.outbound),
          ].join(' - ')
        : '',
    item_brand: offerData?.tourOperator?.code || '',
    item_upselling_type: upsellingUpgradeIds.join(','),
    item_upselling_price:
      upsellingOfferPrice == null ? 0 : upsellingOfferPrice.amount,
    item_altflight_price:
      alternativeFlightPrice == null ? 0 : alternativeFlightPrice.amount,
    index,
  };
};
